import {
  useState,
  useCallback,
  useMemo,
  useEffect,
  ChangeEvent,
} from 'react';
import BroadcastQueueItemHeader from './BroadcastQueueItemHeader';
import BroadcastQueueItemBody from './BroadcastQueueItemBody';
import S from './BroadcastQueueStyles';
import type { BroadcastQueueItem as BroadcastQueueItemType, AbortBroadcastParams, DeviceRow } from './types';
import { QUEUED_TAB_ID, statusToTabId } from './constants';
import useSearchBroadcast from '../../../../API/hooks/announcement/useSearchBroadcast';
import { toast } from '../../../../utils/toast';

const DEVICE_STATUS_PLACEHOLDER = '—';

type Props = {
  fleetId: string | null;
  item: BroadcastQueueItemType;
  isExpanded: boolean;
  onToggle: () => void;
  onAbort: (params: AbortBroadcastParams) => void;
  playingId: string | null;
  onPlayPause: (itemId: string, isPlaying: boolean) => void;
  isAbortLoading?: boolean;
};

export default function BroadcastQueueItem({
  fleetId,
  item,
  isExpanded,
  onToggle,
  onAbort,
  playingId,
  onPlayPause,
  isAbortLoading = false,
}: Props): JSX.Element {
  const [searchValue, setSearchValue] = useState('');
  const [submittedSearchQuery, setSubmittedSearchQuery] = useState('');
  const [activeTabId, setActiveTabId] = useState(item.tabs[0]?.id ?? '');
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([]);

  const {
    data: searchData,
    isLoading: isSearchLoading,
    isError: isSearchError,
    error: searchError,
  } = useSearchBroadcast(
    fleetId,
    item.audioAnnouncementId ?? null,
    item.id,
    submittedSearchQuery,
  );

  const isSearchActive = submittedSearchQuery.length > 0;

  useEffect(() => {
    if (isSearchActive && isSearchError) {
      let message = 'Search failed. Please try again.';
      if (searchError instanceof Error) {
        message = searchError.message;
      } else if (typeof searchError === 'object' && searchError != null && 'message' in searchError) {
        message = String((searchError as { message: unknown }).message);
      }
      toast.error(message);
    }
  }, [isSearchActive, isSearchError, searchError]);

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>): void => {
    setSearchValue(e.target.value);
  }, []);

  const handleSearchSubmit = useCallback((query: string): void => {
    const q = query.trim();
    if (q.length > 0) {
      setSearchValue(q);
      setSubmittedSearchQuery(q);
    }
  }, []);

  const handleClearSearch = useCallback((): void => {
    setSearchValue('');
    setSubmittedSearchQuery('');
  }, []);

  const searchResultRows = useMemo((): DeviceRow[] | null => {
    if (!isSearchActive || searchData == null) return null;
    const data = searchData.data ?? [];
    return data.map((d) => ({
      id: `${d.deviceId}-${d.requestNum}`,
      deviceId: d.deviceId,
      messageStatus: d.status,
      deviceStatus: DEVICE_STATUS_PLACEHOLDER,
    }));
  }, [isSearchActive, searchData]);

  const searchTotalRecords = searchData?.totalRecords ?? 0;

  const queuedDeviceIds = useMemo((): string[] => {
    const source = searchResultRows != null ? searchResultRows : (item.devices ?? []);
    return source
      .filter((d) => statusToTabId(d.messageStatus) === QUEUED_TAB_ID)
      .map((d) => d.deviceId);
  }, [searchResultRows, item.devices]);

  const queuedSet = useMemo(() => new Set(queuedDeviceIds), [queuedDeviceIds]);

  useEffect(() => {
    setSelectedDeviceIds((prev) => prev.filter((id) => queuedSet.has(id)));
  }, [queuedSet]);

  const selectedQueuedIds = useMemo(
    () => selectedDeviceIds.filter((id) => queuedSet.has(id)),
    [selectedDeviceIds, queuedSet],
  );

  const handleAbort = useCallback((): void => {
    const deviceIds = selectedQueuedIds.length > 0 ? selectedQueuedIds : queuedDeviceIds;
    if (deviceIds.length === 0) return;
    onAbort({
      sendId: item.id,
      audioAnnouncementId: item.audioAnnouncementId ?? '',
      deviceIds,
    });
  }, [item.id, item.audioAnnouncementId, queuedDeviceIds, selectedQueuedIds, onAbort]);

  const handleSelectionChange = useCallback((deviceId: string, checked: boolean): void => {
    setSelectedDeviceIds((prev) => {
      if (checked) {
        return prev.includes(deviceId) ? prev : [...prev, deviceId];
      }
      return prev.filter((id) => id !== deviceId);
    });
  }, []);

  const hasQueuedDevices = queuedDeviceIds.length > 0;

  const devicesToShow = useMemo((): DeviceRow[] => {
    if (searchResultRows != null) return searchResultRows;
    if (isSearchActive) return [];
    return item.devices ?? [];
  }, [searchResultRows, isSearchActive, item.devices]);

  return (
    <S.AccordionPanel>
      <BroadcastQueueItemHeader
        filename={item.filename}
        summary={item.summary}
        isExpanded={isExpanded}
        onToggle={onToggle}
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        onSearchSubmit={handleSearchSubmit}
        onClearSearch={handleClearSearch}
        isSearchActive={isSearchActive}
        isSearchLoading={isSearchLoading}
        onAbort={handleAbort}
        abortCount={selectedQueuedIds.length}
        abortDisabled={!hasQueuedDevices || isAbortLoading}
        audioUrl={item.presignedUrl}
        itemId={item.id}
        playingId={playingId}
        onPlayPause={onPlayPause}
      />
      {isExpanded && (
        <BroadcastQueueItemBody
          fleetId={fleetId}
          tabs={item.tabs}
          devices={devicesToShow}
          activeTabId={activeTabId}
          onTabChange={setActiveTabId}
          selectedDeviceIds={selectedDeviceIds}
          onSelectionChange={handleSelectionChange}
          searchResults={searchResultRows != null
            ? { rows: searchResultRows, total: searchTotalRecords }
            : null}
          isSearchActive={isSearchActive}
          isSearchLoading={isSearchLoading}
        />
      )}
    </S.AccordionPanel>
  );
}
